import * as React from 'react';
import type { TarayiciBaglam } from '@hotelcms/shared';
import { Button } from '@/components/ui/button';
import { GirisEkrani } from '@/features/auth/GirisEkrani';
import { ApiHatasi, api, apiForm, oturum } from '@/lib/api';
import { kucultVeJpeg, videodanKare } from '@/lib/gorsel';
import { useKamera } from '@/lib/kamera';
import { BasariRozeti, KimlikRehberi } from '@/features/checkin/KimlikRehberi';

/**
 * Telefon tarafi: /tarayici#KOD
 *
 * Resepsiyonistin kendi telefonu. TEK isi vardir: kimligi cekip masaya
 * gondermek. Bilerek yapmadiklarimiz:
 *
 *   - Oda planini AYNALAMAZ. Telefon ekraninda tesisin doluluk tablosunu
 *     gostermek, kaybolan bir telefonu tesisin tamamina acilan bir pencere
 *     yapardi. Yalnizca masanin o an ustunde oldugu misafir gorunur.
 *   - Check-in'i TAMAMLAMAZ. "Gonder" dedikten sonra is masaya doner;
 *     KBS bildirimini ve oda/ucret onayini yapan tek ekran orasidir.
 *   - Okunan kimlik bilgilerini EKRANA BASMAZ. Gorevli belgeyi zaten elinde
 *     tutuyor; ad ve TCKN'yi telefonda gostermek omuz ustunden okunmaktan
 *     baska bir sey yaramaz.
 *
 * Oturum zorunludur: QR tek basina yetki vermez.
 */

type Durum = 'eslesiyor' | 'hazir' | 'gonderiyor' | 'gonderildi' | 'kapandi' | 'hata';

/**
 * Hangi yuz okutuluyor.
 *
 * Yeni cipli TC kimlik kartinda ANNE ADI, BABA ADI ve DOGUM YERI arka
 * yuzdedir; anne ve baba adi KBS'de zorunludur. Yalnizca on yuz okutulunca
 * bu alanlar bos geliyor ve gorevli masada elle yaziyordu - yani kimlik
 * okumanin butun kazanci kayboluyordu. Bu yuzden akis iki adimlidir ve
 * arka yuz VARSAYILAN OLARAK ISTENIR; pasaportta acikca atlanabilir.
 */
type Yuz = 'on' | 'arka';

export function TelefonTarayici() {
  const [oturumVar, setOturumVar] = React.useState(Boolean(oturum.token()));
  const [kod] = React.useState(() => location.hash.replace(/^#/, '').trim().toUpperCase());

  if (!kod) return <Bilgi baslik="Kare kod okunamadı" metin="Masadaki ekranda çıkan kare kodu telefonunuzun kamerasıyla okutun." />;
  if (!oturumVar) {
    return (
      <div className="min-h-dvh bg-zemin">
        <p className="mx-auto max-w-md px-5 pt-6 text-center text-[13px] text-slate-600">
          Kimlik taramak için önce kendi personel hesabınızla giriş yapın.
        </p>
        <GirisEkrani onGiris={() => setOturumVar(true)} />
      </div>
    );
  }
  return <Tarayici kod={kod} />;
}

function Tarayici({ kod }: { kod: string }) {
  const [durum, setDurum] = React.useState<Durum>('eslesiyor');
  const [baglam, setBaglam] = React.useState<TarayiciBaglam | null>(null);
  const [hata, setHata] = React.useState<string | null>(null);
  const [tenantId, setTenantId] = React.useState<string | null>(null);
  const [yuz, setYuz] = React.useState<Yuz>('on');
  /** On yuz cekildi, arka yuz bekleniyor. Ikisi TEK istekte gonderilir. */
  const [onYuz, setOnYuz] = React.useState<Blob | null>(null);
  /** Kisa omurlu "alindi" rozeti; ekrani kilitlemez, kart cevrilirken soner. */
  const [basari, setBasari] = React.useState(false);

  /*
   * Kamera kancasi. `<video>` HER ZAMAN DOM'da durur; onceden yalnizca
   * "kamera" durumunda basiliyordu ve akis baglanacagi anda ref null
   * olabiliyordu - iPhone'da izin verilse bile goruntu hic gelmiyordu.
   */
  const kamera = useKamera();
  const kamerayiKapat = kamera.kapat;

  /*
   * Eslesme. Kod TEK KULLANIMLIK oldugu icin istek de tam olarak bir kez
   * gitmelidir: StrictMode gelistirmede etkileri iki kez calistirir ve
   * ikinci cagri kendi kodumuzu yakip "kullanilmis kod" hatasi verirdi.
   */
  const eslesildiRef = React.useRef(false);
  React.useEffect(() => {
    if (eslesildiRef.current) return;
    eslesildiRef.current = true;
    let iptal = false;
    (async () => {
      try {
        const [b, ben] = await Promise.all([
          api<TarayiciBaglam>('/api/tarayici/eslestir', {
            method: 'POST',
            body: JSON.stringify({ kod }),
          }),
          api<{ kullanici: { tenantId: string } }>('/api/auth/ben'),
        ]);
        if (iptal) return;
        setBaglam(b);
        setTenantId(ben.kullanici.tenantId);
        setDurum('hazir');
        // Adres cubugunda kod kalmasin: yenilenirse ikinci kez eslesmeye
        // calisir ve "kullanilmis kod" hatasi alirdi.
        history.replaceState(null, '', location.pathname);
      } catch (e) {
        if (iptal) return;
        setDurum('hata');
        setHata(e instanceof ApiHatasi ? e.message : 'Eşleşme kurulamadı.');
      }
    })();
    return () => {
      iptal = true;
    };
  }, [kod]);

  /*
   * Masa hangi misafirdeyse telefon da orada dursun; masa kapaninca ekran
   * kilitlensin. Kimlik verisi bu kanaldan gecmez.
   */
  React.useEffect(() => {
    if (!baglam || !tenantId) return;
    const token = oturum.token();
    if (!token) return;

    const ws = new WebSocket(
      `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws/${tenantId}?token=${token}`,
    );
    ws.onmessage = (olay) => {
      const m = JSON.parse(olay.data as string) as {
        tip: string;
        eslesmeId?: string;
        sira?: number;
        acik?: boolean;
      };
      if (m.tip !== 'TARAYICI_DURUM' || m.eslesmeId !== baglam.eslesmeId) return;
      if (m.acik === false) {
        kamerayiKapat();
        setDurum('kapandi');
        return;
      }
      if (typeof m.sira === 'number') setBaglam((b) => (b ? { ...b, sira: m.sira! } : b));
    };
    const ping = setInterval(() => ws.readyState === WebSocket.OPEN && ws.send('ping'), 30_000);
    return () => {
      clearInterval(ping);
      ws.close();
    };
  }, [baglam?.eslesmeId, tenantId, kamerayiKapat]);

  async function kamerayiAc() {
    setHata(null);
    setDurum('hazir');
    await kamera.ac();
  }

  /** Iki yuz TEK OCR istegiyle gider; model ikisini birlestirip tek kayit doner. */
  async function gonder(on: Blob, arka: Blob | null) {
    if (!baglam) return;
    setDurum('gonderiyor');
    setHata(null);
    try {
      const form = new FormData();
      form.append('gorsel', on, 'kimlik-on.jpg');
      if (arka) form.append('arkaYuz', arka, 'kimlik-arka.jpg');
      form.append('sira', String(baglam.sira));
      await apiForm(`/api/tarayici/oturum/${baglam.eslesmeId}/kimlik`, form);
      kamerayiKapat();
      setOnYuz(null);
      setYuz('on');
      setDurum('gonderildi');
    } catch (e) {
      setDurum('hazir');
      setHata(e instanceof ApiHatasi ? e.message : 'Gönderilemedi. Tekrar deneyin.');
    }
  }

  /** Cekilen kareyi sirasina gore ya bekletir ya da ikisini birden yollar. */
  async function kareAl(kare: Blob) {
    if (yuz === 'on') {
      setOnYuz(kare);
      setYuz('arka');
      setHata(null);
      /*
       * "Başarılı — şimdi arka yüzü taratın" sahnesi 3 sn surer; kilavuz kart
       * perde sonerken (2,1 sn) cevrilir, yani perde kalktiginda ekran zaten
       * arka yuz adiminda olur. Sahne ekrani kilitlemez: gorevli beklemeden
       * fotograf cekebilir.
       */
      setBasari(true);
      window.setTimeout(() => setBasari(false), 3000);
      return;
    }
    await gonder(onYuz!, kare);
  }

  async function cek() {
    if (!kamera.videoRef.current || !kamera.acik) return;
    try {
      await kareAl(await videodanKare(kamera.videoRef.current));
    } catch {
      setHata('Kamera görüntüsü henüz hazır değil. Bir saniye bekleyip tekrar deneyin.');
    }
  }

  async function dosyaSecildi(olay: React.ChangeEvent<HTMLInputElement>) {
    const dosya = olay.target.files?.[0];
    olay.target.value = '';
    if (!dosya) return;
    try {
      await kareAl(await kucultVeJpeg(dosya));
    } catch {
      setHata('Bu dosya okunamadı. Fotoğrafı yeniden çekin ya da JPEG/PNG seçin.');
    }
  }

  /** Pasaportta arka yuz yoktur; gorevli acikca atlayabilsin. */
  async function arkasiYok() {
    if (onYuz) await gonder(onYuz, null);
  }

  if (durum === 'eslesiyor') return <Bilgi baslik="Bağlanıyor…" metin="Masadaki ekranla eşleşiliyor." />;
  if (durum === 'hata') {
    return <Bilgi baslik="Bağlanamadı" metin={hata ?? 'Eşleşme kurulamadı.'} kotu />;
  }
  if (durum === 'kapandi') {
    return (
      <Bilgi
        baslik="Eşleşme kapandı"
        metin="Masadaki check-in ekranı kapatıldı. Yeniden taramak için oradan yeni bir kare kod isteyin."
      />
    );
  }

  const cokKisi = (baglam?.kisiSayisi ?? 1) > 1;

  return (
    <div className="flex min-h-dvh flex-col bg-lacivert text-white">
      {/* Ust bant: gorevlinin gormesi gereken TEK baglam */}
      <header className="flex items-center gap-3 px-4 pt-[max(14px,env(safe-area-inset-top))] pb-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold">
            {cokKisi ? `${(baglam?.sira ?? 0) + 1}. misafir` : 'Kimlik tara'}
            {cokKisi && <span className="text-white/50"> / {baglam?.kisiSayisi}</span>}
          </p>
          <p className="truncate text-[12.5px] text-white/60">
            {baglam?.rezKodu}
            {baglam?.odaNo ? ` · Oda ${baglam.odaNo}` : ''}
          </p>
        </div>
        <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[12px] text-white/80">
          <span className="size-1.5 rounded-full bg-emerald-400" />
          Masaya bağlı
        </span>
      </header>

      {/* Iki adimli akis: hangi yuzde oldugu her an belli olmali */}
      {durum !== 'gonderildi' && (
        <ol className="flex items-center gap-2 px-4 pb-3 text-[12.5px]">
          {(['on', 'arka'] as Yuz[]).map((y, i) => {
            const gecti = y === 'on' && yuz === 'arka';
            const simdi = yuz === y;
            return (
              <li key={y} className="flex flex-1 items-center gap-2">
                <span
                  className={
                    'flex size-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ' +
                    (gecti ? 'bg-emerald-500 text-white' : simdi ? 'bg-white text-lacivert' : 'bg-white/15 text-white/60')
                  }
                >
                  {gecti ? '✓' : i + 1}
                </span>
                <span className={simdi ? 'font-semibold text-white' : 'text-white/55'}>
                  {y === 'on' ? 'Ön yüz' : 'Arka yüz'}
                </span>
                {i === 0 && <span className="h-px flex-1 bg-white/15" />}
              </li>
            );
          })}
        </ol>
      )}

      <main className="flex flex-1 flex-col gap-4 px-4 pb-[max(16px,env(safe-area-inset-bottom))]">
        <div className="relative flex-1 overflow-hidden rounded-2xl bg-black">
          {/*
            <video> KOSULSUZ basilir - kamera acilirken ref hazir olsun diye.
            Onceden yalnizca "kamera" durumunda basiliyordu; akis baglanacagi
            anda React heniz DOM'u islememis olabiliyordu ve iPhone'da izin
            verilse bile goruntu hic gelmiyordu.
          */}
          {/*
            Katmanlar MUTLAK konumlanir. `size-full` burada ise yaramaz:
            kapsayici `min-h-dvh` bir sutunun `flex-1` cocugu oldugu icin
            KESIN bir yuksekligi yok, dolayisiyla `h-full` "auto"ya duser ve
            "Masaya gönderildi" kutusu cerceveyi doldurmayip tepede kaliyordu.
          */}
          <video
            ref={kamera.videoRef}
            autoPlay
            playsInline
            muted
            className={'absolute inset-0 size-full object-cover ' + (kamera.acik ? '' : 'hidden')}
          />

          {kamera.acik ? (
            <>
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="h-[46%] w-[86%] rounded-xl border-4 border-white/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
              </div>
              {/* Hayalet kart: belgeyi nereye tutacagi bakar bakmaz anlasilsin */}
              <div className="pointer-events-none absolute inset-0 text-white">
                <KimlikRehberi yuz={yuz} />
              </div>
              <p className="pointer-events-none absolute inset-x-0 top-3 text-center text-[13.5px] font-semibold drop-shadow">
                {yuz === 'on' ? 'Ön yüzü çerçeveye yerleştirin' : 'Kartı çevirin, arka yüzü gösterin'}
              </p>
              {basari && <BasariRozeti />}
              {durum === 'gonderiyor' && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70">
                  <span className="size-10 animate-spin rounded-full border-4 border-white/30 border-t-white" />
                  <p>Okunuyor…</p>
                </div>
              )}
            </>
          ) : kamera.aciliyor ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <span className="size-10 animate-spin rounded-full border-4 border-white/30 border-t-white" />
              <p>Kamera açılıyor…</p>
            </div>
          ) : durum === 'gonderildi' ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-emerald-600 p-6 text-center">
              <span className="flex size-16 items-center justify-center rounded-full bg-white/20 text-3xl">✓</span>
              <p className="text-xl font-semibold">Masaya gönderildi</p>
              <p className="max-w-xs text-sm text-white/80">
                Bilgiler resepsiyondaki ekrana düştü. Check-in orada tamamlanır.
              </p>
            </div>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center text-white/75">
              <div className="text-white">
                <KimlikRehberi yuz={yuz} akista />
              </div>
              <div className="flex flex-col items-center gap-2">
                <p className="text-lg font-semibold text-white">
                  {yuz === 'on' ? 'Kimliği çerçeveye yerleştirin' : 'Harika — şimdi kartı çevirelim'}
                </p>
                <p className="max-w-xs text-sm">
                  {yuz === 'on'
                    ? 'Fotoğraf telefonda kalmaz; okunup masadaki ekrana gider.'
                    : 'Anne ve baba adı arka yüzde yazıyor. Onu da alalım ki masada hiçbir alan elle yazılmasın.'}
                </p>
              </div>
              {basari && <BasariRozeti />}
            </div>
          )}
        </div>

        {(hata ?? kamera.hata) && (
          <p className="rounded-xl bg-amber-400/15 px-4 py-3 text-[13.5px] leading-relaxed text-amber-200">
            {hata ?? kamera.hata}
          </p>
        )}

        {/* Eylemler basparmak bolgesinde: telefonu tek elle tutup cekilir */}
        {durum === 'gonderildi' ? (
          <div className="flex flex-col gap-2.5">
            <Button boyut="xl" className="w-full" onClick={kamerayiAc}>
              Sıradaki kimliği tara
            </Button>
            <p className="text-center text-[12.5px] text-white/55">
              Masadaki ekran hangi misafirdeyse burası da orada olur.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {kamera.acik ? (
              <Button boyut="xl" className="w-full" onClick={cek} yukleniyor={durum === 'gonderiyor'}>
                {yuz === 'on' ? 'Ön yüzü çek' : 'Arka yüzü çek ve gönder'}
              </Button>
            ) : (
              <>
                <Button boyut="xl" className="w-full" onClick={kamerayiAc} yukleniyor={kamera.aciliyor}>
                  {kamera.hata ? 'Kamerayı tekrar dene' : yuz === 'on' ? 'Kamerayı aç' : 'Arka yüz için kamerayı aç'}
                </Button>
                <Button
                  boyut="xl"
                  varyant="ikincil"
                  className="w-full"
                  onClick={() => document.getElementById('tarayici-dosya')?.click()}
                >
                  Galeriden seç
                </Button>
                <input
                  id="tarayici-dosya"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  hidden
                  onChange={dosyaSecildi}
                />
              </>
            )}

            {/*
              Pasaportta arka yuz yoktur. Atlamayi GIZLEMIYORUZ ama one de
              cikarmiyoruz: varsayilan yol iki yuzu de okutmak.
            */}
            {yuz === 'arka' && (
              <div className="flex items-center justify-between gap-3 pt-0.5">
                <button
                  onClick={() => {
                    setOnYuz(null);
                    setYuz('on');
                  }}
                  className="text-[13px] text-white/60 underline underline-offset-4"
                >
                  Ön yüzü tekrar çek
                </button>
                <button
                  onClick={arkasiYok}
                  className="text-[13px] text-white/60 underline underline-offset-4"
                >
                  Arka yüzü yok (pasaport)
                </button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function Bilgi({ baslik, metin, kotu }: { baslik: string; metin: string; kotu?: boolean }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-lacivert px-6 text-center text-white">
      <div className="max-w-sm">
        <h1 className={'text-xl font-semibold ' + (kotu ? 'text-amber-300' : '')}>{baslik}</h1>
        <p className="mt-2 text-[14px] leading-relaxed text-white/70">{metin}</p>
      </div>
    </div>
  );
}
